import { useState, useCallback, useMemo } from "react";
import { createContainer } from "unstated-next";
import Pluralize from "pluralize";
import { rangeParamsPairs , fetchSearch } from "../../utils/search";
import ArticleSearchContainer from "./ArticleSearchContainer";
import camelCase from "../../utils/camelCase";
import I18n from "../../../../app/javascripts/lib/i18n-js/ja";


const useFormItemsContainer = (initialState = {}) => {
  const [items, setItems] = useState(initialState.items);
  const popularRelatedLinks = initialState.popularRelatedLinks || [];
  const articleSearch = ArticleSearchContainer.useContainer();
  const {volOrJob} = articleSearch;
  const {aggs} = articleSearch;

  const mainVolSearchItemNames = ["kind", "genre", "position"];
  const mainJobSearchItemNames = ["working_form", "genre", "job_type"];

  // サイドバーの「条件で絞り込む」の項目
  const subVolIdItemNames = ["characteristic", "atmosphere", "skill", "status"];
  const subJobIdItemNames = ["job_characteristic", "status"];
  const subRangeItemNames = Object.keys(rangeParamsPairs);
  const subPriceItemNames = ["price_category"];

  const itemHash = (item) => {
    const name = `${item}`;
    return {
      name,
      title: I18n.t(`${item}_name`),
      aggs: aggs[`${item}_count`][`${item}`],
      ids: articleSearch[`${camelCase(item)}Ids`],
      setIds: articleSearch[`set${camelCase(item, { pascalCase: true })}Ids`],
      items: items[`${Pluralize(item)}`],
    };
  };

  // NOTE: title(表示ラベル)での引き当ては文言変更で静かに壊れるため、range/price/date にも安定キーの name を持たせる
  const priceItemHash = (item) => ({
    name: `${item}`,
    title: I18n.t(`${item}_name`),
    aggs: aggs[`${item}_count`][`${item}`],
    priceId: articleSearch.priceId,
    setPriceId: articleSearch.setPriceId,
    items: items[`${Pluralize(item)}`],
  });

  const dateItemHash = {
    name: "date",
    title: "活動日程",
    startDate: articleSearch.startDate,
    endDate: articleSearch.endDate,
    setStartDate: articleSearch.setStartDate,
    setEndDate: articleSearch.setEndDate,
  };

  const rangeItemHash = (item) => {
    const value = rangeParamsPairs[item];
    return {
      name: `${item}`,
      title: I18n.t(`${item}_name`),
      gte: articleSearch[`${camelCase(value)}Gte`],
      lte: articleSearch[`${camelCase(value)}Lte`],
      setGte: articleSearch[`set${camelCase(value, { pascalCase: true })}Gte`],
      setLte: articleSearch[`set${camelCase(value, { pascalCase: true })}Lte`],
      items: items[`${Pluralize(item)}`],
    };
  };

  const mainSearchItems =
    volOrJob === "job"
      ? mainJobSearchItemNames.map((item) => itemHash(item))
      : mainVolSearchItemNames.map((item) => itemHash(item));

  const subVolSearchItems = [
    ...subVolIdItemNames.map((item) => itemHash(item)),
    ...subRangeItemNames.map((item) => rangeItemHash(item)),
    ...subPriceItemNames.map((item) => priceItemHash(item)),
    dateItemHash,
  ];

  const subJobSearchItems = [
    ...subJobIdItemNames.map((item) => itemHash(item)),
    ...subRangeItemNames.map((item) => rangeItemHash(item)),
  ];

  const subSearchItems =
    volOrJob == "job" ? subJobSearchItems : subVolSearchItems;

  const searchItems = mainSearchItems.concat(subSearchItems);

  // spのみで使用
  const setChildAreas = useCallback(async (parentAreaId) => {
    const url = Routes.api_v1_prefecture_cities_path(parentAreaId);
    const res = await fetchSearch(url);
    const newItems = { ...items };
    newItems.areas.forEach((area) => {
      area.opts.forEach((opt) => {
        if (opt.value == parentAreaId) {
          // eslint-disable-next-line no-param-reassign
          opt.opts = res.cities;
        }
      });
    });
    setItems(newItems);
  }, []);

  const cityIds = useMemo(() => {
    try {
      if (!items || !items.areas) {
        return [];
      }

      return Object.values(items.areas).flatMap((regionArray) => regionArray.flatMap((region) => {
          if (!region.opts) {
            return [];
          }
          return region.opts.filter((pref) => Array.isArray(pref.opts)).flatMap((pref) => pref.opts.map((city) => city.value));
        }));
    } catch (e) {
      return [];
    }
  }, [items]);

  return {
    items,
    // pc
    mainSearchItems,
    subSearchItems,
    // sp
    searchItems,
    cityIds,
    popularRelatedLinks,
    // functions
    setItems,
    setChildAreas,
  };
};

const FormItemsContainer = createContainer(useFormItemsContainer);

export default FormItemsContainer;
